import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Eraser } from "lucide-react";

/**
 * Canvas para firmar con dedo o ratón.
 * - `value` (dataURL PNG) se reproduce dentro del canvas si existe.
 * - `onChange(dataUrl)` se dispara al soltar el trazo.
 */
export default function SignaturePad({
    value,
    onChange,
    label,
    testId,
    height = 120,
}) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const [hasInk, setHasInk] = useState(!!value);

    // Initialize: paint stored value
    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        // Scale for retina
        const dpr = window.devicePixelRatio || 1;
        const rect = c.getBoundingClientRect();
        if (c.width !== rect.width * dpr) {
            c.width = rect.width * dpr;
            c.height = rect.height * dpr;
            ctx.scale(dpr, dpr);
        }
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#1f2937";
        // Paint stored signature
        if (value) {
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, c.width, c.height);
                ctx.drawImage(img, 0, 0, rect.width, rect.height);
                setHasInk(true);
            };
            img.src = value;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getXY = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        if (e.touches && e.touches.length > 0) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top,
            };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawingRef.current = true;
        const ctx = canvasRef.current.getContext("2d");
        const { x, y } = getXY(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const move = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const { x, y } = getXY(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        setHasInk(true);
        const dataUrl = canvasRef.current.toDataURL("image/png");
        onChange?.(dataUrl);
    };

    const clear = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        setHasInk(false);
        onChange?.("");
    };

    return (
        <div className="space-y-1.5">
            {label && (
                <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest font-bold text-slate-500">
                        {label}
                    </span>
                    {hasInk && (
                        <button
                            type="button"
                            onClick={clear}
                            className="text-[10px] uppercase font-bold text-slate-400 hover:text-rose-600 inline-flex items-center gap-1"
                            data-testid={`${testId}-clear`}
                        >
                            <Eraser className="w-3 h-3" />
                            Borrar
                        </button>
                    )}
                </div>
            )}
            <div
                className="relative rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden"
                style={{ height }}
            >
                <canvas
                    ref={canvasRef}
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                    className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
                    data-testid={testId}
                    style={{ width: "100%", height: "100%" }}
                />
                {!hasInk && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-slate-300 pointer-events-none">
                        Firma aquí
                    </span>
                )}
            </div>
        </div>
    );
}
